// src/features/leases/selectors/rentSchedule.js
//
// LEASE-RENT-SCHEDULE — pure, memoized selector that builds the per-year
// rent schedule from the lease object and the separately-fetched allowances.
//
// Inputs:
//   lease      — the active-lease summary from GET /api/locations/:locId/lease.
//                Must include yearCosts (from lease_year_costs) and computed block.
//                Optional: leaseStart / leaseEnd (ISO date strings) to detect
//                partial first/last years and annualise Rate/SF correctly.
//   allowances — array from GET .../allowances (full allowance objects with type,
//                totalAmount, expiryDate). Only type='abatement' rows contribute.
//   today      — Date; defaults to new Date(). Used to identify the current year.
//
// Output: ordered array of calendar-year rows:
//   { year, months, isPartial, partialFrom, partialTo,
//     ratePsf, contractual, abatement, net, escPct }
//
//   months      — 1–12; 12 for full years, actual month count for partial years.
//   isPartial   — true when the row covers fewer than 12 months.
//   partialFrom — first month number (1-based) in the partial range, or null.
//   partialTo   — last  month number (1-based) in the partial range, or null.
//   ratePsf     — annualised (contractual * 12 / months) / RSF so partial last/
//                 first years show the true $/sf rate, not the prorated dollar ÷ RSF.
//   escPct      — YoY change in ratePsf (not raw contractual), so a partial year
//                 that has a smaller dollar total still shows the true rate step.
//
// Fallback: when lease has no lease_year_costs rows, returns a single synthetic
// row for the current year using computed.annualRent (from getActiveLeaseSummary).
// Never uses leases.total_cost to derive annual rent; never fabricates escalation steps.
//
// This is the canonical front-end computation; the server mirrors it for KPI
// derivation (see server/routes/leases.js GET /lease) so the values agree.

import { convertAmount } from "../../../utils/fxConvert.js";
import { applyMonthlyPayableYears, monthlyPayableEvidence } from "../../../../shared/monthlyPayable.js";

/**
 * Build the per-year rent schedule for display.
 *
 * @param {object}   lease      Active-lease summary (includes yearCosts + computed).
 * @param {object[]} allowances All allowances for the lease (pre-fetched).
 * @param {Date}     [today]    Reference date (defaults to new Date()).
 * @returns {Array<{year:number, months:number, isPartial:boolean,
 *                  partialFrom:number|null, partialTo:number|null,
 *                  ratePsf:number|null, contractual:number,
 *                  abatement:number, net:number, escPct:number|null,
 *                  isFallback?:boolean}>}
 */
import { annualAreaBasis } from "../../../shared/leaseAnnualHeadlines.js";

export function buildRentSchedule(lease, allowances, today) {
  const ref = today instanceof Date ? today : new Date();

  const yearCosts = (lease?.yearCosts || [])
    .filter((yc) => !yc.isProjected)
    .sort((a, b) => Number(a.year) - Number(b.year));
  const sizeNum   = lease?.size != null ? Number(lease.size) : null;

  if (yearCosts.length === 0) {
    if (monthlyPayableEvidence(lease).state === "empty") return _buildFallbackRow(lease, sizeNum, ref);
    return _completeLeaseTermRows(applyMonthlyPayableYears(
      lease?.monthlyCosts?.length ? [] : _buildFallbackRow(lease, sizeNum, ref), lease), lease);
  }

  const abatementByYear = _buildAbatementByYear(allowances);

  // Derive partial-year boundaries from lease start/end dates.
  const rawLeaseStart = lease?.leaseStart ?? lease?.startDate;
  const rawLeaseEnd   = lease?.leaseEnd ?? lease?.endDate;
  const leaseStart  = rawLeaseStart ? new Date(rawLeaseStart) : null;
  const leaseEnd    = rawLeaseEnd   ? new Date(rawLeaseEnd)   : null;
  const startYear   = leaseStart ? leaseStart.getFullYear() : null;
  const endYear     = leaseEnd   ? leaseEnd.getFullYear()   : null;
  const startMonth  = leaseStart ? leaseStart.getMonth() + 1 : null; // 1-12
  const endMonth    = leaseEnd   ? leaseEnd.getMonth()   + 1 : null; // 1-12

  // First pass: compute per-row months + annualised ratePsf.
  // EDIT-LEASE-MODAL-PHASE2B: preserve ycAbatement from the row so the second
  // pass can prefer it over the allowances-derived value when non-zero.
  const intermediate = yearCosts.map((yc) => {
    let months      = 12;
    let isPartial   = false;
    let partialFrom = null;
    let partialTo   = null;

    if (endYear && yc.year === endYear && endMonth != null && endMonth < 12) {
      months      = endMonth;
      isPartial   = true;
      partialFrom = 1;
      partialTo   = endMonth;
    } else if (startYear && yc.year === startYear && startMonth != null && startMonth > 1) {
      months      = 13 - startMonth;
      isPartial   = true;
      partialFrom = startMonth;
      partialTo   = 12;
    }

    const contractual   = yc.rent == null ? null : Number(yc.rent);
    const ratePsf       = contractual != null && sizeNum && sizeNum > 0
      ? (contractual * 12 / months) / sizeNum
      : null;
    const premisesSqft  = yc.premisesSqft != null ? Number(yc.premisesSqft) : null;
    // Row-level abatement from lease_year_costs (may be 0 when not yet set).
    const ycAbatement   = yc.abatement != null ? Number(yc.abatement) : 0;

    return {
      year: yc.year,
      months,
      isPartial,
      partialFrom,
      partialTo,
      contractual,
      ratePsf,
      premisesSqft,
      ycAbatement,
      currency: yc.currency || lease?.currency || "USD",
    };
  });

  // Second pass: attach abatement, net, and rate-based escPct.
  // Abatement precedence: row.ycAbatement > 0 → use it; else fall back to
  // the allowances-derived value. This preserves backward compatibility for
  // leases whose abatement came from the allowances table import.
  const sourcedRows = intermediate.map((row, i) => {
    const usedYc    = row.ycAbatement > 0;
    const abatement = usedYc ? row.ycAbatement : (abatementByYear[row.year] || 0);
    const net       = row.contractual == null ? null : row.contractual - abatement;
    // abatementSource distinguishes where the abatement value came from:
    //   "yc"        → set directly on the lease_year_costs row
    //   "allowance" → derived from the allowances table (abatement type)
    //   null        → no abatement (abatement = 0)
    const abatementSource = abatement > 0 ? (usedYc ? "yc" : "allowance") : null;

    let escPct = null;
    if (i > 0) {
      const prev        = intermediate[i - 1];
      const prevRatePsf = prev.ratePsf;
      const currRatePsf = row.ratePsf;

      // Suppress ESC% when either boundary row is a partial calendar year —
      // the dollar totals are not comparable so the rate step is inflated.
      const partialBoundary = row.isPartial || prev.isPartial;

      // Suppress ESC% when premises footprint changed between rows.
      // If premisesSqft is null on either row the footprint check is skipped
      // (no regression for leases without this data).
      const footprintChanged = (
        row.premisesSqft != null &&
        prev.premisesSqft != null &&
        row.premisesSqft !== prev.premisesSqft
      );

      const consecutiveCalendarYears = Number(row.year) === Number(prev.year) + 1;
      if (!partialBoundary && !footprintChanged && consecutiveCalendarYears &&
          prevRatePsf != null && currRatePsf != null && prevRatePsf > 0) {
        escPct = (currRatePsf - prevRatePsf) / prevRatePsf * 100;
      }
    }

    return {
      year:            row.year,
      currency:        row.currency,
      months:          row.months,
      isPartial:       row.isPartial,
      partialFrom:     row.partialFrom,
      partialTo:       row.partialTo,
      ratePsf:         annualAreaBasis(lease, Number(row.year), row) > 0 && row.contractual != null
        ? row.contractual / annualAreaBasis(lease, Number(row.year), row) : null,
      contractual:     row.contractual,
      abatement,
      abatementSource,
      net,
      escPct,
    };
  });

  return _completeLeaseTermRows(applyMonthlyPayableYears(sourcedRows, lease), lease);
}

/**
 * Resolve only source-backed month detail for one annual schedule row.
 *
 * Allowance expiry dates are treated as exact credit months only when their
 * summed amounts match the annual allowance-derived abatement. Critical-date
 * start/end pairs can substantiate a rent-free period or an abatement window,
 * but never a monthly dollar split.
 */
export function buildRentScheduleMonthlyDetails(row, allowances = [], criticalDates = []) {
  const year = Number(row?.year);
  const months = Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    label: MONTH_LABELS[index],
    kind: "standard",
    creditAmount: 0,
  }));
  if (!Number.isInteger(year)) {
    return { state: "none", months: [], windows: [], exactCredits: [] };
  }

  const datedCredits = (allowances || [])
    .filter((allowance) => allowance?.type === "abatement")
    .map((allowance) => {
      const date = _parseMonthDate(allowance.expiryDate);
      const amount = Number(allowance.totalAmount);
      if (!date || date.year !== year || !Number.isFinite(amount) || amount <= 0) return null;
      return { month: date.month, amount, date: date.iso };
    })
    .filter(Boolean);
  const datedCreditTotal = datedCredits.reduce((total, credit) => total + credit.amount, 0);
  const annualAbatement = Number(row?.abatement);
  const hasMatchingAllowanceTotal = datedCredits.length > 0 &&
    Number.isFinite(annualAbatement) &&
    Math.abs(datedCreditTotal - annualAbatement) <= Math.max(0.02, annualAbatement * 0.000001);
  const exactCredits = row?.abatementSource !== "yc" && hasMatchingAllowanceTotal
    ? [...datedCredits.reduce((byMonth, credit) => {
        const existing = byMonth.get(credit.month);
        if (existing) {
          existing.amount += credit.amount;
          if (credit.date < existing.date) existing.date = credit.date;
        } else {
          byMonth.set(credit.month, { ...credit });
        }
        return byMonth;
      }, new Map()).values()]
    : [];

  for (const credit of exactCredits) {
    const month = months[credit.month - 1];
    month.kind = "credit";
    month.creditAmount += credit.amount;
  }

  const windows = _resolveRentWindows(criticalDates);
  let hasFreeRent = false;
  let hasUnsplitWindow = windows.some((window) =>
    window.kind === "window" && window.end.year >= year && window.start.year <= year
  );
  for (const window of windows) {
    for (const monthNumber of _monthsInYearWindow(window, year)) {
      const month = months[monthNumber - 1];
      if (month.kind === "credit") continue;
      if (window.kind === "free") {
        if (_windowCoversEntireMonth(window, year, monthNumber)) {
          month.kind = "free";
          hasFreeRent = true;
        } else {
          month.kind = "window";
          hasUnsplitWindow = true;
        }
      } else if (month.kind !== "free") {
        month.kind = "window";
        hasUnsplitWindow = true;
      }
    }
  }

  // Do not expose other years' critical dates in this row's details.
  const rowWindows = windows.filter((window) =>
    window.end.year >= year && window.start.year <= year
  );
  const state = hasUnsplitWindow
    ? "dated_window"
    : exactCredits.length > 0 || hasFreeRent
    ? "exact_months"
    : Number.isFinite(annualAbatement) && annualAbatement > 0
      ? "annual_only"
      : "none";

  return { state, months, windows: rowWindows, exactCredits };
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function _parseMonthDate(value) {
  if (!value) return null;
  const iso = value instanceof Date
    ? (Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : "")
    : String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day
  ) return null;
  return { year, month, day, iso };
}

function _resolveRentWindows(criticalDates) {
  const types = {
    free: ["free_rent_start", "free_rent_end"],
    window: ["rent_abatement_start", "rent_abatement_end"],
  };
  const windows = [];
  for (const [kind, [startType, endType]] of Object.entries(types)) {
    const starts = [...new Set((criticalDates || [])
      .filter((date) => date?.type === startType)
      .map((date) => _parseMonthDate(date.dueDate))
      .filter(Boolean)
      .map((date) => date.iso))];
    const ends = [...new Set((criticalDates || [])
      .filter((date) => date?.type === endType)
      .map((date) => _parseMonthDate(date.dueDate))
      .filter(Boolean)
      .map((date) => date.iso))];
    // Critical-date rows do not carry a pairing key. If more than one unique
    // start or end exists, choosing a pair would invent a relationship.
    if (starts.length !== 1 || ends.length !== 1) continue;
    const start = _parseMonthDate(starts[0]);
    const end = _parseMonthDate(ends[0]);
    if (start && end && start.iso <= end.iso) windows.push({ kind, start, end });
  }
  return windows;
}

function _monthsInYearWindow(window, year) {
  if (year < window.start.year || year > window.end.year) return [];
  const from = year === window.start.year ? window.start.month : 1;
  const to = year === window.end.year ? window.end.month : 12;
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

function _windowCoversEntireMonth(window, year, month) {
  const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
  const monthEnd = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  return window.start.iso <= monthStart && window.end.iso >= monthEnd;
}

/**
 * Derive the three schedule-based KPIs from a rent schedule.
 * These must match what FinancialTermsCard shows (computed block).
 *
 * @param {Array}  schedule     Output of buildRentSchedule.
 * @param {number} currentYear  e.g. new Date().getFullYear()
 * @returns {{ annualRentBilled: number|null, annualRentRunRate: number|null,
 *             totalLifetimeCost: number|null }}
 */
export function scheduleKpis(schedule, currentYear, displayCurrency, rates) {
  const sourcedSchedule = (schedule || []).filter((row) => !row.isNotInSource);
  if (sourcedSchedule.length === 0) {
    return { annualRentBilled: null, annualRentRunRate: null, totalLifetimeCost: null };
  }
  const targetCurrency = displayCurrency || sourcedSchedule.find((r) => r.currency)?.currency || "USD";
  const currentRow = sourcedSchedule.find((r) => r.year === currentYear);
  const annualRentBilled  = currentRow != null
    ? _convertForAggregate(currentRow.net, currentRow.currency || targetCurrency, targetCurrency, rates)
    : null;
  // Compatibility key: this is the recorded calendar-year contractual total,
  // not an annualized comparison rate. Keep first/last stubs exactly as sourced.
  const headline = currentRow?.basis === "monthly_actual" ? currentRow.net : currentRow?.contractual;
  const annualRentRunRate = headline != null
    ? _convertForAggregate(
        headline,
        currentRow.currency || targetCurrency,
        targetCurrency,
        rates,
      )
    : null;
  const totalLifetimeCost = sourcedSchedule[0]?.isFallback
    ? null
    : (() => {
        let total = 0;
        for (const row of sourcedSchedule) {
          const converted = _convertForAggregate(
            row.net,
            row.currency || targetCurrency,
            targetCurrency,
            rates,
          );
          if (converted == null) return null;
          total += converted;
        }
        return total;
      })();
  return { annualRentBilled, annualRentRunRate, totalLifetimeCost };
}

// ─── Internals ────────────────────────────────────────────────────────────────

function _completeLeaseTermRows(sourcedRows, lease) {
  const rawLeaseStart = lease?.leaseStart ?? lease?.startDate;
  const lastSourcedYear = sourcedRows.reduce(
    (latest, row) => Math.max(latest, Number(row.year)),
    -Infinity,
  );
  const leaseStart = _parseLeaseDate(rawLeaseStart);
  const rawLeaseEnd = lease?.leaseEnd ?? lease?.endDate;
  const leaseEnd = _parseLeaseDate(rawLeaseEnd);
  const hasValidTerm = leaseStart && leaseEnd &&
    (leaseStart.year < leaseEnd.year ||
      (leaseStart.year === leaseEnd.year && leaseStart.month <= leaseEnd.month));

  // Keep the historical Spacebase suffix behavior for payloads that do not
  // include a start date. For leases with both valid term dates, source coverage
  // is determined by the actual year-cost rows, independent of source/import.
  const yearsToFill = new Set();
  if (hasValidTerm && leaseEnd.year < 9999) {
    for (let year = leaseStart.year; year <= leaseEnd.year; year += 1) {
      yearsToFill.add(year);
    }
  } else if (
    !leaseStart &&
    lease?.lastCsvImportedAt &&
    lastSourcedYear === 2030 &&
    leaseEnd &&
    leaseEnd.year > 2030 &&
    leaseEnd.year < 9999
  ) {
    for (let year = 2031; year <= leaseEnd.year; year += 1) {
      yearsToFill.add(year);
    }
  }

  if (yearsToFill.size === 0) return sourcedRows;

  const rowsByYear = new Map(sourcedRows.map((row) => [Number(row.year), row]));
  for (const year of yearsToFill) {
    if (rowsByYear.has(year)) continue;
    const isTermYear = Boolean(hasValidTerm && year >= leaseStart.year && year <= leaseEnd.year);
    const firstMonth = isTermYear && year === leaseStart.year ? leaseStart.month : 1;
    const lastMonth = leaseEnd && year === leaseEnd.year ? leaseEnd.month : 12;
    const months = lastMonth - firstMonth + 1;
    const isPartial = months < 12;
    rowsByYear.set(year, {
      year,
      currency: lease?.currency || "USD",
      months,
      isPartial,
      partialFrom: isPartial ? firstMonth : null,
      partialTo: isPartial ? lastMonth : null,
      ratePsf: null,
      contractual: null,
      abatement: null,
      abatementSource: null,
      net: null,
      escPct: null,
      isNotInSource: true,
      sourceStatus: "not_in_source",
    });
  }

  return [...rowsByYear.values()].sort((a, b) => Number(a.year) - Number(b.year));
}

function _parseLeaseDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    return { year: value.getUTCFullYear(), month: value.getUTCMonth() + 1 };
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    !Number.isFinite(parsed.getTime()) ||
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day
  ) return null;
  return { year, month };
}

function _buildAbatementByYear(allowances) {
  const map = {};
  for (const al of (allowances || [])) {
    if (al.type !== "abatement") continue;
    const raw = al.expiryDate;
    if (!raw) continue;
    const yr = new Date(raw).getFullYear();
    if (!Number.isFinite(yr)) continue;
    map[yr] = (map[yr] || 0) + Number(al.totalAmount || 0);
  }
  return map;
}

function _buildFallbackRow(lease, sizeNum, ref) {
  const computed = lease?.computed || {};
  const annualRent = computed.annualRent ?? computed.annualRentBilled ?? null;
  if (annualRent == null) return [];
  const year    = ref.getFullYear();
  // A fallback has no annual-period area evidence.
  const ratePsf = null;
  return [{
    year,
    currency: lease?.currency || "USD",
    months:      12,
    isPartial:   false,
    partialFrom: null,
    partialTo:   null,
    ratePsf,
    contractual: annualRent,
    abatement:   0,
    net:         annualRent,
    escPct:      null,
    isFallback:  true,
  }];
}

function _convertForAggregate(amount, fromCurrency, toCurrency, rates) {
  return convertAmount(amount, fromCurrency, toCurrency, rates);
}
